import { api } from "@/lib/api/client";

/**
 * Analítica de la app «Mixcore» y la música (backend#116):
 * GET /api/nexus/analytics/* — permiso dashboard:analytical:view.
 * Las cifras se resumen cada noche (04:30 Chile); los períodos terminan ayer.
 * Diseño: mixcore-mobile/docs/analitica/ANALITICA.md.
 */

export type AppPeriod = "7d" | "28d" | "90d";

export interface Kpi {
  value: number;
  previous: number | null;
  change: number | null; // razón: 0.25 = +25 %
}

export interface PeriodInfo {
  key: string;
  from: string;
  to: string;
  previousFrom?: string | null;
  previousTo?: string | null;
}

export interface Overview {
  period: PeriodInfo;
  kpis: Record<string, Kpi>;
  sessionsBySource: Record<string, number> | null;
  peopleByPlatform: Record<string, number> | null;
  series: Array<{ date: string; activePeople: number; plays: number; sessions: number }>;
}

export interface ArtistRow {
  artistaId: number;
  nombre: string | null;
  plays: number;
  listeners: number;
  minutes: number;
  newFollowers: number;
  likes: number;
  playlistAdds: number;
  profileViews: number;
}

export interface Descubre {
  period: PeriodInfo;
  starts: number;
  genres: Array<{ name: string; count: number }>;
  countries: Array<{ name: string; count: number }>;
  cities: Array<{ name: string; count: number }>;
  tracksPerArtist: Array<{ name: string; count: number }>;
}

export interface ContentRow {
  id: number;
  title: string;
  slug: string;
  type: string;
  views: number;
  readers: number;
  fromApp: number;
}

export type ArtistSort = "plays" | "listeners" | "minutes" | "followers" | "likes" | "views";

export async function fetchOverview(period: AppPeriod): Promise<Overview> {
  const { data } = await api.get<Overview>("/api/nexus/analytics/overview", { params: { period } });
  return data;
}

export async function fetchArtists(period: AppPeriod, sort: ArtistSort): Promise<ArtistRow[]> {
  const { data } = await api.get<{ artists: ArtistRow[] }>("/api/nexus/analytics/artists", {
    params: { period, sort, limit: 25 },
  });
  return data.artists || [];
}

export async function fetchDescubre(period: AppPeriod): Promise<Descubre> {
  const { data } = await api.get<Descubre>("/api/nexus/analytics/descubre", { params: { period } });
  return data;
}

export async function fetchContent(period: AppPeriod): Promise<ContentRow[]> {
  const { data } = await api.get<{ pieces: ContentRow[] }>("/api/nexus/analytics/content", { params: { period } });
  return data.pieces || [];
}
